import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { parse } from 'date-fns';
import { Info, Trash2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';

import { ConsignmentLineDetailResponse } from '@/entities/consignments';
import { FormField, DatePicker, SimpleSelect, FormSelect } from '@/shared/ui';
import { DATE_FORMAT, formatDateForApi } from '@/shared/ui/date-range-picker';
import { ConfirmationAction } from '@/features/confirmation-action';

import { useConsignmentLineDetailsForm } from '../model';
import { ConsignmentLineDetailsFormSkeleton } from './form-skeleton';
import { DensityValidationAlert } from './density-validation-alert';
import { OptionalFieldsSection } from './optional-fields-section';
import { ColdChainFields } from './cold-chain-fields';

interface ConsignmentLineDetailsFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (lineDetails: ConsignmentLineDetailResponse) => void;
  onDeleteSuccess?: () => void;
  lineId?: string;
  consignmentId?: string;
  mode?: 'create' | 'edit' | 'view';
  canDeleteLine?: boolean;
}

export const ConsignmentLineDetailsFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  onDeleteSuccess,
  lineId,
  consignmentId,
  mode = 'view',
  canDeleteLine = false
}: ConsignmentLineDetailsFormModalProps) => {
  const { t } = useTranslation();

  const {
    form,
    productCategoryOptions,
    inventoryUnitOptions,
    handlingUnitOptions,
    dangerousGoodsOptions,
    conversionRateInfo,
    optionalFields,
    subProjectOptions,
    isLoading,
    isFetching,
    isSaving,
    isResetting,
    isDeleting,
    isViewMode,
    handleSubmit,
    handleDelete,
    handleClose
  } = useConsignmentLineDetailsForm({
    lineId,
    consignmentId,
    mode,
    onSuccess,
    onDeleteSuccess,
    onClose
  });

  const {
    control,
    formState: { errors, isDirty },
    register,
    watch
  } = form;

  const totalWeight = watch('totalWeight');
  const totalVolume = watch('totalVolume');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-xl lg:max-w-4xl max-h-[80vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('newcmr.linedetail.box')}</DialogTitle>
        </DialogHeader>

        <form
          id="consignment-line-details-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          className="py-4">
          {isLoading || isFetching || isResetting ? (
            <ConsignmentLineDetailsFormSkeleton />
          ) : (
            <FieldGroup className="grid gap-4 lg:grid-cols-2 lg:items-start">
              <div className="flex flex-col gap-4">
                <FormField
                  label={t('product.description.label')}
                  name="productCategory"
                  error={!isViewMode ? errors.productCategory?.message : undefined}
                  required>
                  <FormSelect
                    name="productCategory"
                    control={control}
                    placeholder="Select category"
                    options={productCategoryOptions}
                    disabled={isViewMode}
                  />
                </FormField>

                <div className="flex flex-row gap-4">
                  <FormField
                    label={t('movement.ltuQty.label')}
                    name="inventoryQuantity"
                    error={!isViewMode ? errors.inventoryQuantity?.message : undefined}
                    className="w-full"
                    required>
                    <Input
                      {...register('inventoryQuantity', { valueAsNumber: true })}
                      type="number"
                      step="1"
                      min={0}
                      id="inventoryQuantity"
                      disabled={isViewMode}
                    />
                  </FormField>

                  <FormField
                    label=""
                    name="productLtuId"
                    error={!isViewMode ? errors.productLtuId?.message : undefined}>
                    <FormSelect
                      name="productLtuId"
                      control={control}
                      placeholder="Unit"
                      options={inventoryUnitOptions}
                      disabled={isViewMode}
                    />
                  </FormField>
                </div>

                <FormField
                  label={t('requestline.ownerItemDesc.label')}
                  name="description"
                  error={!isViewMode ? errors.description?.message : undefined}
                  required>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) =>
                      isViewMode ? (
                        <Input {...field} disabled={true} />
                      ) : (
                        <Textarea
                          {...field}
                          rows={1}
                          className="min-h-9 py-1.5"
                          placeholder={t('requestline.ownerItemDesc.desc')}
                        />
                      )
                    }
                  />
                </FormField>

                {subProjectOptions.length > 0 ? (
                  <FormField
                    label={t('subproject.idnf.label')}
                    name="subProjectCode"
                    required={subProjectOptions.length > 1}
                    error={!isViewMode ? errors.subProjectCode?.message : undefined}>
                    <FormSelect
                      name="subProjectCode"
                      control={control}
                      placeholder={t('validity.valueMissing.select')}
                      options={subProjectOptions}
                      disabled={isViewMode}
                    />
                  </FormField>
                ) : (
                  isViewMode && (
                    <FormField label={t('subproject.idnf.label')} name="subProjectCode">
                      <Input
                        {...register('subProjectCode')}
                        id="subProjectCode"
                        disabled={true}
                        placeholder={t('validity.valueMissing.select')}
                      />
                    </FormField>
                  )
                )}

                <div className="flex gap-4">
                  <FormField
                    label={t('bundle.bundleQty.label')}
                    name="handlingUnits"
                    error={!isViewMode ? errors.handlingUnits?.message : undefined}
                    className="w-full"
                    required>
                    <Input
                      {...register('handlingUnits', { valueAsNumber: true })}
                      type="number"
                      step="1"
                      id="handlingUnits"
                      disabled={isViewMode}
                    />
                  </FormField>

                  <FormField
                    label=""
                    name="handlingUnitTypeId"
                    error={!isViewMode ? errors.handlingUnitTypeId?.message : undefined}>
                    <FormSelect
                      name="handlingUnitTypeId"
                      control={control}
                      placeholder="Unit"
                      options={handlingUnitOptions}
                      disabled={isViewMode}
                    />
                  </FormField>
                </div>

                {conversionRateInfo && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>{conversionRateInfo}</AlertDescription>
                  </Alert>
                )}

                <FormField
                  label={t('tracking.servicereq.totweight.label')}
                  name="totalWeight"
                  error={!isViewMode ? errors.totalWeight?.message : undefined}
                  required>
                  <Input
                    {...register('totalWeight', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    id="totalWeight"
                    disabled={isViewMode}
                  />
                </FormField>

                <FormField
                  label={t('tracking.servicereq.totalVolume.label')}
                  name="totalVolume"
                  error={!isViewMode ? errors.totalVolume?.message : undefined}
                  required>
                  <Input
                    {...register('totalVolume', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    id="totalVolume"
                    disabled={isViewMode}
                  />
                </FormField>

                <DensityValidationAlert weight={totalWeight} volume={totalVolume} isViewMode={isViewMode} />

                <FormField
                  label={t('requestline.insuranceValue.label')}
                  name="totalValue"
                  error={!isViewMode ? errors.totalValue?.message : undefined}>
                  <Input
                    {...register('totalValue', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    id="totalValue"
                    disabled={isViewMode}
                    placeholder={t('requestline.insuranceValue.desc')}
                  />
                </FormField>
              </div>

              <div className="flex flex-col gap-4">
                <FormField
                  label={t('requestline.ownerItemIdentifier.label')}
                  name="ownerReference"
                  error={!isViewMode ? errors.ownerReference?.message : undefined}>
                  <Input
                    {...register('ownerReference')}
                    id="ownerReference"
                    disabled={isViewMode}
                    placeholder={t('requestline.ownerItemIdentifier.desc')}
                  />
                </FormField>

                <FormField
                  label={t('requestline.expiryDate.label')}
                  name="expiryDate"
                  error={!isViewMode ? errors.expiryDate?.message : undefined}>
                  <Controller
                    name="expiryDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value ? parse(field.value, DATE_FORMAT, new Date()) : null}
                        onChange={(date) => field.onChange(date ? formatDateForApi(date) : '')}
                        dateFormat="dd/MMM/yyyy"
                        id="expiryDate"
                        disabled={isViewMode}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label={t('requestline.batchNumber.label')}
                  name="batchNumber"
                  error={!isViewMode ? errors.batchNumber?.message : undefined}>
                  <Input
                    {...register('batchNumber')}
                    id="batchNumber"
                    disabled={isViewMode}
                    placeholder={t('requestline.batchNumber.label')}
                  />
                </FormField>

                <OptionalFieldsSection
                  control={control}
                  errors={errors}
                  optionalFields={optionalFields}
                  isViewMode={isViewMode}
                />

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-4">
                      <Field orientation="horizontal">
                        <Controller
                          name="fragile"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="fragile"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isViewMode}
                            />
                          )}
                        />
                        <FieldLabel
                          htmlFor="fragile"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('requestline.isFragile.label')}
                        </FieldLabel>
                      </Field>

                      <Field orientation="horizontal">
                        <Controller
                          name="regulated"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="regulated"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isViewMode}
                            />
                          )}
                        />
                        <FieldLabel
                          htmlFor="regulated"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('requestline.isRegulated.label')}
                        </FieldLabel>
                      </Field>
                    </div>

                    <div className="space-y-4">
                      <Field orientation="horizontal">
                        <Controller
                          name="dangerous"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="dangerous"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isViewMode}
                            />
                          )}
                        />
                        <FieldLabel
                          htmlFor="dangerous"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('requestline.isDangerous.label')}
                        </FieldLabel>
                      </Field>

                      <Field orientation="horizontal">
                        <Controller
                          name="coldChain"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="coldChain"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isViewMode}
                            />
                          )}
                        />
                        <FieldLabel
                          htmlFor="coldChain"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('requestline.isColdStorage.label')}
                        </FieldLabel>
                      </Field>
                    </div>
                  </div>

                  <ColdChainFields form={form} isViewMode={isViewMode} />
                  {/* Dangerous Goods Selector - shows when dangerous checkbox is checked */}
                  {watch('dangerous') && (
                    <FormField
                      label={t('requestline.dangerousGoodsUnid.label')}
                      name="dangerousGoodsUnNumber"
                      error={!isViewMode ? errors.dangerousGoodsUnNumber?.message : undefined}>
                      <Controller
                        name="dangerousGoodsUnNumber"
                        control={control}
                        render={({ field }) => (
                          <SimpleSelect
                            options={dangerousGoodsOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('gen.autocomplete.title')}
                            disabled={isViewMode}
                            className="w-full"
                          />
                        )}
                      />
                    </FormField>
                  )}
                </div>

                <FormField
                  label={t('requestline.handlingInstructions.label')}
                  name="handlingInstructions"
                  error={!isViewMode ? errors.handlingInstructions?.message : undefined}>
                  <Controller
                    name="handlingInstructions"
                    control={control}
                    render={({ field }) =>
                      isViewMode ? <Input {...field} disabled={true} /> : <Textarea {...field} className="min-h-20" />
                    }
                  />
                </FormField>

                <Field orientation="horizontal">
                  <Controller
                    name="showOnPackingList"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="showOnPackingList"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isViewMode}
                      />
                    )}
                  />
                  <FieldLabel
                    htmlFor="showOnPackingList"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {t('requestline.isOnPackingList.label')}
                  </FieldLabel>
                </Field>
              </div>
            </FieldGroup>
          )}

          <div className="flex justify-between pt-6 mt-6 border-t">
            <div>
              {canDeleteLine && mode === 'edit' && (
                <ConfirmationAction
                  trigger={
                    <ActionButton
                      type="button"
                      intent="danger"
                      className="min-w-[120px]"
                      disabled={isSaving || isDeleting}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('gen.delete.label')}
                    </ActionButton>
                  }
                  title={t('consignment.line.delete.title', 'Delete Consignment Line')}
                  description={t(
                    'consignment.line.delete.description',
                    'Are you sure you want to delete this consignment line? This action cannot be undone.'
                  )}
                  confirmText={t('gen.delete.label')}
                  cancelText={t('gen.cancel.label')}
                  confirmIntent="danger-confirm"
                  onConfirm={handleDelete}
                  isLoading={isDeleting}
                  loadingText={t('common.deleting', 'Deleting...')}
                />
              )}
            </div>
            <div className="flex gap-3">
              {isViewMode ? (
                <ActionButton intent="dismiss" type="button" onClick={handleClose} className="min-w-[120px]">
                  {t('gen.closedialog.label')}
                </ActionButton>
              ) : (
                <>
                  <ActionButton
                    type="button"
                    intent="dismiss"
                    onClick={handleClose}
                    className="min-w-[120px]"
                    disabled={isSaving}>
                    {t('gen.closedialog.label')}
                  </ActionButton>
                  <ActionButton
                    intent="primary"
                    type="submit"
                    className="min-w-[120px]"
                    disabled={isSaving || !isDirty}
                    form="consignment-line-details-form">
                    {isSaving ? t('common.saving') : t('newcmr.save.label')}
                  </ActionButton>
                </>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
